//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { findIndex, get, isEqual, map, find, has } from "lodash";
import React, { useMemo, useState, useEffect, useCallback, useRef} from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
//  -- Constants ----------------------
import { UserOutlined } from '@ant-design/icons';
import { Collapse } from 'antd';
import MessageRenderer from 'components/MessageRenderer';
import { lighten } from 'polished';
import { useCurrentUser } from 'store/users/selectors';
import { Pinpoint } from 'utils/assets/images';
import { getAvatar } from 'utils/assets/images/lootie';
import { useActivities } from 'store/activities/selectors';
import { Modal , Button as MtButton, Box, CircularProgress } from '@mui/material';
import { Close, WatchLater, InfoOutlined, AnalyticsOutlined } from '@mui/icons-material';
import COLOR from 'utils/constants/color';
import ModalMenu from "./Modals";
import AiGradingButton from "./AiGradingButton";
import {v4} from "uuid";
import { CaretDownOutlined } from '@ant-design/icons';
import { useClassActivities } from "store/classActivities/selectors";
import { useClassQuizzes } from "store/classQuizzes/selectors";
import {GradeDisplayPreview, MatchMakingReview } from "features/forms/assignment";
import GridView from "modules/bingoModule/teacher/gridView";
import { Alert } from '@mui/material';
import QuestionPreview from "modules/workSheet/Assignment/QuestionPreview";
import { WORK_SESSION_VIEW_BUTTON_LABEL } from 'components/StudentAssignmentTracking/studentAssignmentTrackingStatic';
import { graphic_data } from "utils/constants/graphic_org";
import AssignmentPhotoCollage from "modules/activityModule/photoCollage";
import { transformToGrid } from "utils/config/reference";
import * as Sentry from '@sentry/react';
import usePermissionForUser from "./hooks/permissions/usePermissionForUser";
import TranscriptWithAudio from "./UI/transcriptWithAudio/transcriptWithAudio";
import { NoteMark } from "./UI";
import { useStandards } from 'store/story/selectors';
import { getDiffConfigForStudent } from "utils/methods/math";
import SentryWrap from 'components/Sentry';
import { getStandardDescription, formatStandardDisplay, formatStateStandardDisplay, getAllStandardAbbrsFromStateStandard, getStateStandardGroupedByState, filterStateStandardByEnabledStates } from 'utils/lib/standardHelper';
import StandardsModal from './StandardsModal';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import AIGradingAndFeedback from './AIGradingAndFeedback';
import { useOrgStandardCodes } from "store/organizations/selectors";
import { useGradeBook } from "./hooks/gradeBook/useGradeBook";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { useCollectionNotification } from "utils/methods/firebaseNotification";
import { handleListClassSubmissions, handleReturnSubmission } from "store/submissions/thunks";
import { isBeforTime } from "utils/methods/math";
import moment from "moment";

const alphaRef = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
const { Panel } = Collapse;

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const AI_Review_message = `The AI is currently reviewing the student’s answer. Grading and feedback will appear within 15 minutes.`
const Answer = styled.span`
    color: ${p => p.$neutral ? 'inherit' : (p.correct ? COLOR.green100 : 'red')};
    display: block;
    font-size: 14px;
    font-weight: 600;
    text-decoration: ${p => p.$neutral ? 'none' : (!p.correct && p.children !== '__' ? 'line-through' : 'none')};
`;

const Button = styled.button`
    align-items: center;
    background-color: transparent;
    border: none;
    color: ${COLOR.black300};
    display: flex;
    flex-direction: row;
    font-size: 14px;
    font-weight: 600;
    justify-content: space-between;
    outline: none;
    padding: 8px;
    text-align: left;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
`;

const ExpandedContainer = styled.div`
    height: ${p => p.expanded ? 'auto' : '0px'};
    margin-top: ${p => p.expanded && '0px'};
    overflow: hidden;
    padding: ${p => p.expanded && '8px'};
    width: 100%;
    padding-bottom: 30px;
`;

const Name = styled.span`
    display: inline-block;
    flex: 1 1 320px;
    min-width: 180px;
    max-width: 100%;
    overflow-wrap: anywhere;
    color:${p => p.isLate ? COLOR.radish100 : COLOR.black}
    @media screen and (max-width: 768px) {
        flex-basis: 100%;
        min-width: 0;
    }
`;

const Question = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
    align-items: center;
    display: flex;
    ul, ol {
        padding-left: 20px;
        margin-bottom: 20px;
    }
`;

const StandardStoryHeading = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
    color: ${COLOR.black};
`;

const StandardStoryContent = styled.span`
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 4px;
    opacity: 0.7;
`;

const Section = styled.div`
    height: auto;
    margin-bottom: 24px;
    width: 100%;
    display: flex;
    flex-direction: column;

    &:last-child {
        margin-bottom: 0px;
    }
`;
const FeedbackSection = styled.div`
    height: auto;
    padding-bottom: 24px;
    width: 100%;
    background-color: rgb(121 120 199);
    padding-left: 24px;
    border-radius: 10px;
`;
const SectionHeader = styled.span`
    border-bottom: 1px dashed ${COLOR.black800};
    color: ${COLOR.green100};
    display: block;
    font-size: 10px;
    font-weight: 600;
    margin-bottom: 8px;
    padding-bottom: 4px;
    text-transform: uppercase;
`;
const FeedbackText = styled.span`
    color: ${COLOR.white};
    display: block;
    font-size: 14px;
    margin: 12px;
    `;

const Subsection = styled.div`
    margin-bottom: 16px;
    width: 100%;

    &:last-child {
        margin-bottom: 0px;
    }
`;

const Text = styled.span`
    color: ${COLOR.green100};
    display: inline-block;
`;

const Wrapper = styled.div`
    border: 1px dashed ${COLOR.black700};
    border-radius: 3px;
    margin-bottom: 8px;
    width: 100%;
    &:last-child {
        margin-bottom: 0px;
    }
`;
const Notification = styled.div`
    background-color: ${p => p.bgColor ? p.bgColor : lighten(0.45, '#3D5F8F')};
    border: 1px dashed ${p => p.bgColor ? p.color : '#3D5F8F'};
    border-radius: 3px;
    color: ${p => p.color ? p.color : '#3D5F8F'};
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 24px;
    padding: 8px;
`;
const AIContent = styled.div`
    text-align: left;
    width: 100%;
    border-radius: 20px;
    // padding-right: 20px;
    // padding-top: 20px;
    // padding-bottom: 10px;
    // padding-left: 30px;
    padding:25px 25px 20px 25px;
    background: rgb(241,235,251);
    font-size: 13px;
    font-weight: 500;
    margin-top: 10px;
`;
const ErrorAiArea = styled.div`
   background:rgb(82 50 128);
   border-radius:10px;
   position: relative;
   color: ${COLOR.white};
   width: 245px;
   padding-left: 10px;
//    height: 26px;
   padding-top: ${p => p.isFeedBack ? "3px" : "1px"};
   margin-top: 25px;
   font-size:${p => p.isFeedBack ? "11px" : "14px"};
`;

const AiGradingLevelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    justify-content: center;
    flex-direction: row;
    padding-left: 10px;
    @media Screen and (max-width: 768px) {
        flex-direction: column;
    }

    @media (min-width: 545px) and (max-width: 768px) {
        flex-wrap: unset;
    }
`;

const AiGradingLevelLabel = styled.p`
    font-weight: 600;
    color: ${COLOR.black100};
    white-space: nowrap;
    margin: 0;
`;

const AiGradingLevelValue = styled.p`
    padding: 4px 12px;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    color: ${COLOR.black100};
    font-weight: 500;
    white-space: nowrap;
    margin: 0;
`;

const QImage = styled.img`
    aspect-ratio: 3 / 2;
    max-width:322px;
    border-radius:15px;
`;
const ImageQuestionArea = styled.div`
  display : flex;
  flex-direction: column;
  col-gap:10px;
  row-gap:10px;
`;
const ImageAnswerArea = styled.div`
  display : flex;
  flex-direction: column;
  col-gap:10px;
  row-gap:10px;
  border: 1px dashed black;
  margin-top: 15px;
  padding: 10px;
  position: relative;
}
`;

const AnswerMarkLagendaArea = styled.div`
  position : absolute;
  left : 10px;
  background-color:white;
  top: -12px;
  padding-left: 5px;
  padding-right: 5px;
`
const PointerContainer = styled.div`
   position : relative;
   background-image:url(${p => p.bg ? `'${p.bg}'` : ''});
   max-width:322px;
   background-size: 100% 100%;
   aspect-ratio :3/2;
   border-radius: 15px;
`;
const GraphicContainer = styled.div`
   position : relative;
   background-image:url(${p => p.bg ? `'${p.bg}'` : ''});
   max-width:322px;
   background-size: 100% 100%;
   aspect-ratio :2/3;
//    cursor:pointer;
   cursor: zoom-in;
`;
const PuzzleWrapper = styled.div`
    background: #102d71;
    padding: 22px 22px;
    width: 100%;
    border-radius: 17px;
    min-height:80px;
    border: 2px solid ${p => p.is_correct ? COLOR.green100 : COLOR.red};
    display: flex;
    flex-direction: column;
    color: #fff;
`;
const PuzzleRow = styled.div`
    display: flex;
    flex-direction: row;
    color: #fff;
`;
const PinPoint = styled.div`
    background: url('${Pinpoint}') no-repeat center bottom / contain;
    width: 30px;
    height: 35px;
    transform: translate(-50%, 0);
    position: absolute;
    bottom: 30px;
    left: 5%;
`;
const PinPointWrapper = styled.div`
    position: absolute;
    left: ${p => p.x ? p.x : 0}%;
    top:  ${p => p.y ? p.y : 0}%;
    cursor: default;
    width: 30px;
    height: 30px;
`;
const AnswerByUserDebate = styled.div`
    font-weight: 400;
    background-color: ${p => p.active ? "rgb(179 255 251 / 60%)" : "transparent"};
    padding: 3px;
    border-radius: 5px;
`;
const SubComment = styled.div`
   padding-left:16px;
`;
const IconImg = styled.img`
    height: 25px;
    width: 25px;
    border-radius: 15px;
    margin-right: 4px;
`;
const CommentIconImg = styled.img`
    height: 20px;
    width: 20px;
    border-radius: 15px;
    margin-right: 4px;
`;
const Container = styled.div`
  display: flex;
`;

const PrintWrapper = styled.div`
  display: flex;
`;


const PlanContainer = styled.div`
  display: flex;
  flex-direction: column;
//   width: 90%;
  max-height: 100%;
  position: relative;
  justify-content: center;
  padding: 20px;
  background-color: white;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media only screen and (max-width: 750px) {
    margin:0px 10px;
  }
`;
const PlanWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
`;
const CloseBtn = styled.div`
  background-color: rgb(204, 80, 71);
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  z-index: 1;
`;
const AnswerWindowWrapper = styled.div`
   position: relative;
   width: 100%;
`;
const Images = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  object-fit: cover;
  height: 600px;
   height: 96vh;

  @media only screen and (max-width: 750px) {
   width:100%;
   height:100%;
  }
`;
const ImageModal = styled.div`
    left: 50%;
    max-width: 544px;
    padding: 0px 24px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
`;
const Image = styled.img`
    border: 1px solid ${COLOR.black800};
    width: 100%;
    height: auto;
`;

const PictureImages = styled.img`
  background-color: ${COLOR.white};
//   box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius:8px;
  height: 100%;
  width: fit-content;
  max-width: 250px;
  max-height: 250px;
`;

const ImagesScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: hidden;
    gap: 15px;
    padding: 10px 0;
    margin-top: 25px;
    width: 100%;
    min-height: 100px;
    max-height: 400px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
        width: 8px;
        -webkit-appearance: none;
        display: block !important;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
        
        &:hover {
            background: #555;
        }
    }
    &::-webkit-scrollbar:vertical {
        width: 8px;
    }

    @media screen and (max-width: 1200px){
        overflow-y: scroll;
    }
`;

const ImageItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;
const GraphicViewBtn = styled.button`
    background-color: #03989e;
    color: #fff;
    padding: 3px 10px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s ease;
    margin-top:3px;
    cursor: pointer;
`;

const MainGraphicViewBtn = styled.div`
    text-align: right;
    margin-right: 3px;
    max-width: 322px;
    margin-bottom: 10px;
`;
//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  -- Description
//  Grade, answers and feedback display
//
//  -- Props
//  ...
//

function isURL(str) {
    const pattern = /^(https?:\/\/|www\.)[^\s\.]+\.\S{2,}$/;
    return pattern.test(str);
}
const game_q_type = {
    quiz: "Quiz",
    true_or_false: "True or false",
    type_answer: "Type Answer",
    puzzle: "Puzzle",
    poll: "Poll",
    drop_pin: "Drop Pin",
    word_cloud: "Word Cloud",
    open_ended: "Open Ended",
    brainstorm: "Brainstorm",
    informational_page: "Informational Page",
}
const getType = (type) => {
    return game_q_type[type] || ''
}
function sortSubmissionsByUserId(submissions, userId) {
    return submissions.sort((a, b) => {
        const aIsUser = a.user_id === userId;
        const bIsUser = b.user_id === userId;

        if (aIsUser && !bIsUser) {
            return -1;
        } else if (!aIsUser && bIsUser) {
            return 1;
        }
        return 0;
    });
}

const AnswerWindow = ({ src, initialFontSize, setInitialFontSize, input_position, label_position, answers ,setImageModalState}) => {
    // const allLocations = [{...input_position, source: 'input'}, ...label_position.map(item => ({...item, source: 'label'}))];
    return (
    <SentryWrap>
        <AnswerWindowWrapper>
            <Images src={src} />
            {input_position?.map((positions, index) => {
                const ans = Array.isArray(answers) ? answers[index] : {};
                const max_height = positions.max_height ? { maxHeight: positions.max_height } : {};
                const min_height = positions.min_height ? { minHeight: positions.min_height } : {};
                return (<div className={`${positions.class_name} scrollViewGrad`} key={index} style={{
                    position: 'absolute',
                    width: `calc(${positions.width} * 100%)`,
                    height: positions.height ? `calc(${positions.height} * 100%)` : '',
                    left: `calc(${positions.left} * 100%)`,
                    top: `calc(${positions.top} * 100%)`,
                    fontSize: `${initialFontSize}px`,
                    wordWrap:"break-word",
                    overflowY:"auto",
                    padding:"5px"
                }}>
                    {ans?.type === 'text' ? ans.answer : <img src={ans?.answer} style={{ width: '100%', height: '100%' , objectFit:"contain" , cursor:"pointer" }} onClick={()=>setImageModalState({open:true,src:ans?.answer})}  />}
                </div>


//  <div className={`${positions.class_name} scrollViewGrad`} style={{
//                             position: 'absolute',
//                             width: `calc(${positions.width} * 100%)`,
//                             height: positions.height ? `calc(${positions.height} * 100%)` : '',
//                             left: `calc(${positions.left} * 100%)`,
//                             top: `calc(${positions.top} * 100%)`,
//                             fontSize:12,
//                             wordWrap:"break-word",
//                             overflowY:"auto",
//                             padding:"5px"
//                         }}>
//                             {ans?.type ==='text'?ans.answer: <img src={ans?.answer} style={{ width: '100%', height: '100%' , cursor:"pointer" , objectFit:"contain" }}/> }
//                         </div>


                )
            })}
            {label_position?.map((positions, index) => {
                const ans = Array.isArray(answers) ? answers[index] : {};
                const max_height = positions.max_height ? { maxHeight: positions.max_height } : {};
                const min_height = positions.min_height ? { minHeight: positions.min_height } : {};
                return (<div className={`${positions.class_name} scrollViewGrad`} key={index} style={{
                    position: 'absolute',
                    width: `calc(${positions.width} * 100%)`,
                    height: positions.height ? `calc(${positions.height} * 100%)` : '',
                    left: `calc(${positions.left} * 100%)`,
                    top: `calc(${positions.top} * 100%)`,
                    fontSize:12,
                    wordWrap:"break-word",
                    overflowY:"auto",
                    padding:"5px"
                }}>
                    {
                        positions.value
                    }
                    {/* {ans?.input_type === 'text' ? ans.value : <img src={ans?.answer} style={{ width: '100%', height: '100%' , objectFit:"contain" , cursor:"pointer" }} onClick={()=>setImageModalState({open:true,src:ans?.answer})}  />} */}
                </div>

                )
            })}
        </AnswerWindowWrapper>
    </SentryWrap>
    );
};

const QuestionComponent = ({ question, index, answer, allQuestions, isStudent, aiFeedback, aiSuggestion, is_game, is_debate, is_worksheet, selected_student_id, is_graphic_organizer, activity, page, setImageModalState, languageData, ai_grade, is_loteria, isFeedBack , renderedGroups , currentUser, pauseAllOtherAudios, audioRefs, not_show_indication = false, rubricsToggle, isCompleted }) => {
    const dispatch = useDispatch();
    const [open,setOpen] = useState(false)
    const [questionImagePreviewModal,setQuestionImagePreviewModal] = useState(false);
    const [selectedQuestionImage, setSelectedQuestionImage] = useState(null);
    const [imagePreviewModal,setImagePreviewModal] = useState(false);
    const [selectedAnswerImage, setSelectedAnswerImage] = useState(null);
    const standards = useStandards();
    const enabledStateCodes = useOrgStandardCodes();
    
    
    const [standardsModal, setStandardsModal] = useState({
        open: false,
        standardAbbrs: [],
        stateStandard: null
    })

    const [initialFontSize, setInitialFontSize] = useState(12);

    const increaseFontSize = () => {
        if (initialFontSize < 24) {
            setInitialFontSize(initialFontSize + 1);
        }
    }
    const decreaseFontSize = () => {
        if (initialFontSize > 9) {
            setInitialFontSize(initialFontSize - 1);
        }
    }
    
    const getStandard = useMemo(() => {
        return (abbr) => {
            if (!abbr || !Array.isArray(standards) || standards.length === 0) return null;
            
            if (typeof abbr === 'string' && abbr.includes(',')) {
                const standardAbbrs = abbr.split(',').map(s => s.trim()).filter(s => s);
                const descriptions = standardAbbrs.map(abbrItem => {
                    const standardObj = find(standards, ["abbr", abbrItem]);
                    return getStandardDescription(standardObj, enabledStateCodes);
                }).filter(desc => desc);
                
                return descriptions.length > 0 ? descriptions.join('\n\n') : null;
            } else {
                const standardObj = find(standards, ["abbr", typeof abbr === 'string' ? abbr.trim() : abbr]);
                return getStandardDescription(standardObj, enabledStateCodes);
            }
        }
    }, [standards, enabledStateCodes]);
    
    const standard = useMemo(() => {
        if (question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0) {
            const allAbbrs = getAllStandardAbbrsFromStateStandard(question.state_standard, enabledStateCodes);
            if (allAbbrs.length > 0) {
                const descriptions = allAbbrs.map(abbrItem => {
                    const standardObj = find(standards, ["abbr", abbrItem]);
                    return getStandardDescription(standardObj, enabledStateCodes);
                }).filter(desc => desc);
                return descriptions.length > 0 ? descriptions.join('\n\n') : null;
            }
        }
        return getStandard(question.standard);
    }, [getStandard, question.standard, question.state_standard, standards, enabledStateCodes]);
    
    const handleStandardInfoClick = useCallback(() => {
        if (question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0) {
            const filteredStateStandard = filterStateStandardByEnabledStates(question.state_standard, enabledStateCodes);
            const standardAbbrs = getAllStandardAbbrsFromStateStandard(filteredStateStandard, enabledStateCodes);
            if (standardAbbrs.length > 0) {
                setStandardsModal({ open: true, standardAbbrs, stateStandard: filteredStateStandard });
                return;
            }
        }
        if (!question.standard) return;
        const standardAbbrs = typeof question.standard === 'string' && question.standard.includes(',')
            ? question.standard.split(',').map(s => s.trim()).filter(s => s)
            : [question.standard];
        setStandardsModal({ open: true, standardAbbrs });
    }, [question.standard, question.state_standard, enabledStateCodes]);
    
    const renderStandardWithInfo = useCallback(() => {
        const hasStateStandard = question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0;
        const hasStandard = question.standard;
        
        if ((!hasStateStandard && !hasStandard) || isStudent) return null;
        
        const formattedStandard = hasStateStandard 
            ? formatStateStandardDisplay(question.state_standard, standards, enabledStateCodes)
            : formatStandardDisplay(question.standard, standards, enabledStateCodes);
        
        if (!formattedStandard) return null;
        
        return (
            <span
                style={{
                    fontWeight: 600,
                    color: "#03989e",
                    whiteSpace: "break-spaces",
                    marginLeft: "6px",
                }}
            >
                {formattedStandard}
                <div className="quesTxtIcon questionStandardAbbrIcon" style={{ display: 'inline-block', marginLeft: '4px' }}>
                    <label style={{ cursor: 'pointer' }}>
                        <InfoOutlined onClick={handleStandardInfoClick} />
                    </label>
                </div>
            </span>
        );
    }, [question.standard, question.state_standard, isStudent, standard, handleStandardInfoClick, standards, enabledStateCodes]);
    
    const questionContent = useMemo(() => {
        if (!question) return null;
        switch (question.type) {
            case 'multipleChoice':
            case 'multipleResponse':
            case 'essay':
            case 'email':
            case 'trueFalse':
            case 'match':
                return <Question className="breakTheSentence" style={{ justifyContent: "space-between" , alignItems: "flex-start"}}>
                    <div style={{display:"flex"}}>
                    <label className="questionInline">{index + 1}.</label ><span className="fillingQues" dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                    </div>
                    {/* Standard */}
                    {(() => {
                        const formattedStandard = (question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0)
                            ? formatStateStandardDisplay(question.state_standard, standards, enabledStateCodes)
                            : formatStandardDisplay(question.standard, standards, enabledStateCodes);
                        
                        if (!formattedStandard || isStudent) return null;
                        
                        return (
                            <span
                                className="questionStandardAbbr"
                            >
                                {formattedStandard}
                                <div className="quesTxtIcon questionStandardAbbrIcon">
                                    <label>
                                        <InfoOutlined onClick={() => {
                                            if (question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0) {
                                                const filteredStateStandard = filterStateStandardByEnabledStates(question.state_standard, enabledStateCodes);
                                                const standardAbbrs = getAllStandardAbbrsFromStateStandard(filteredStateStandard, enabledStateCodes);
                                                setStandardsModal({ open: true, standardAbbrs, stateStandard: filteredStateStandard });
                                            } else {
                                                const standardAbbrs = typeof question.standard === 'string' && question.standard.includes(',')
                                                    ? question.standard.split(',').map(s => s.trim()).filter(s => s)
                                                    : [question.standard];
                                                setStandardsModal({ open: true, standardAbbrs });
                                            }
                                        }} />
                                    </label>
                                </div>
                            </span>
                        );
                    })()}
                    </Question>;
            case 'audio':
            case 'audioEssay':
            case 'twoMinAudio':
            case 'audioTrueFalse':
                return <Question className="breakTheSentence" style={{ justifyContent: "flex-start" , alignItems: "flex-start", display:"flex", flexWrap:"wrap"}}>
                    <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", width: "100%", minWidth: 0, gap: "0.25em" }}><label style={{ flexShrink: 0 }}>{index + 1}.</label> 
                {question?.question && (
                    <div style={{ flex: 1, minWidth: 0 }} dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                )}
                </div>
                {question.audio && (() => {
                    const audioKey = `question-${index}-${question.audio}`;
                    if (!audioRefs.current.has(audioKey)) {
                        audioRefs.current.set(audioKey, React.createRef());
                    }
                    const audioRef = audioRefs.current.get(audioKey);
                    return (
                        <TranscriptWithAudio audioUrl={question.audio} title={question.question}> 
                            <audio 
                                ref={audioRef}
                                controls 
                                controlsList={ "nodownload"} 
                                src={question.audio}
                                onPlay={() => pauseAllOtherAudios(audioKey)}
                            /> 
                        </TranscriptWithAudio>
                    );
                })()}
                {/* Standard */}
                {renderStandardWithInfo()}
                </Question>;
            case 'audioToImage':
                return <Question className="breakTheSentence" style={{ justifyContent: "flex-start" , alignItems: "flex-start", display:"flex", flexWrap:"wrap"}}>
                    <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", width: "100%", minWidth: 0, gap: "0.25em" }}><label style={{ flexShrink: 0 }}>{index + 1}.</label> 
                {question?.question && (
                    <div style={{ flex: 1, minWidth: 0 }} dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                )}
                </div>
                {question.audio && (() => {
                    const audioKey = `question-${index}-${question.audio}`;
                    if (!audioRefs.current.has(audioKey)) {
                        audioRefs.current.set(audioKey, React.createRef());
                    }
                    const audioRef = audioRefs.current.get(audioKey);
                    return (
                        <TranscriptWithAudio audioUrl={question.audio} title={question.question}> 
                            <audio 
                                ref={audioRef}
                                controls 
                                controlsList={ "nodownload"} 
                                src={question.audio}
                                onPlay={() => pauseAllOtherAudios(audioKey)}
                            /> 
                        </TranscriptWithAudio>
                    );
                })()}
                {renderStandardWithInfo()}
                {question.standard && !isStudent && (
                <span
                style={{
                    fontWeight: 600,
                    color: "#03989e",
                    whiteSpace: "break-spaces",
                    marginLeft: "6px",
                    
                }}
                >
                {question.standard}
                </span>)}
                </Question>;
            case 'audioMultipleChoice':
                const groupAudio = question.audio;
                if (renderedGroups.has(question.groupKey)) {
                    return <Question className="breakTheSentence 333" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}><span style={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", width: "100%", minWidth: 0, gap: "0.25em" }}><label style={{ flexShrink: 0 }}>{index + 1}.</label><span style={{ flex: 1, minWidth: 0 }} dangerouslySetInnerHTML={{ __html: question.question || '' }} /></span>
                    {/* Standard */}
                  
                    {(() => {
                        const audioKey = `question-${index}-${groupAudio}`;
                        if (!audioRefs.current.has(audioKey)) {
                            audioRefs.current.set(audioKey, React.createRef());
                        }
                        const audioRef = audioRefs.current.get(audioKey);
                        return (
                            <TranscriptWithAudio audioUrl={groupAudio} title={question.question}>
                                <audio 
                                    ref={audioRef}
                                    controls 
                                    controlsList={ "nodownload"} 
                                    src={groupAudio}
                                    onPlay={() => pauseAllOtherAudios(audioKey)}
                                /> 
                            </TranscriptWithAudio>
                        );
                    })()}
                      
                    {((question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0) || question.standard) && !isStudent && (
                    <span
                    style={{
                        fontWeight: 600,
                        color: "#03989e",
                        whiteSpace: "break-spaces",
                        marginLeft: "6px",
                        
                    }}
                    >
                    {question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0
                        ? formatStateStandardDisplay(question.state_standard, standards, enabledStateCodes)
                        : formatStandardDisplay(question.standard, standards, enabledStateCodes)}
                    </span>)}
                    </Question>
                } else {
                    renderedGroups.add(question.groupKey);
                    return (
                        <Question className="breakTheSentence 444" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}><span style={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", width: "100%", minWidth: 0, gap: "0.25em" }}><label style={{ flexShrink: 0 }}>{index + 1}.</label><span style={{ flex: 1, minWidth: 0 }} dangerouslySetInnerHTML={{ __html: question.question || '' }} /></span>
                            {(() => {
                                const audioKey = `question-${index}-${groupAudio}`;
                                if (!audioRefs.current.has(audioKey)) {
                                    audioRefs.current.set(audioKey, React.createRef());
                                }
                                const audioRef = audioRefs.current.get(audioKey);
                                return (
                                    <TranscriptWithAudio audioUrl={groupAudio} title={question.question}>
                                        <audio 
                                            ref={audioRef}
                                            controls 
                                            controlsList={ "nodownload"} 
                                            src={groupAudio}
                                            onPlay={() => pauseAllOtherAudios(audioKey)}
                                        /> 
                                    </TranscriptWithAudio>
                                );
                            })()}
                        </Question>
                    );
                }
            case 'picture':
                const allQuestionImages = [];
                if (question?.pictures) {
                    if (Array.isArray(question.pictures)) {
                        allQuestionImages.push(...question.pictures);
                    } else {
                        allQuestionImages.push(question.pictures);
                    }
                }
                if (question?.additionalPictures && Array.isArray(question.additionalPictures)) {
                    allQuestionImages.push(...question.additionalPictures);
                }

                return (
                    <>
                        {question?.question && (
                            <div className="question-title-on-grade" style={{ justifyContent: "flex-start" , alignItems: "flex-start", display: "flex"}}><label>{index + 1}.</label> <span dangerouslySetInnerHTML={{ __html: question?.question || '' }} /></div>
                        )}

                        <Question className="breakTheSentence">
                            {question?.question?.length === 0 && `${index + 1}.`} 
                            {allQuestionImages.length > 0 && (
                                <>
                                    {allQuestionImages.length === 1 ? (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'start',
                                            alignItems: 'start',
                                        }}>
                                            <PictureImages 
                                                src={allQuestionImages[0]} 
                                                onClick={() => {
                                                    setSelectedQuestionImage(allQuestionImages[0]);
                                                    setQuestionImagePreviewModal(true);
                                                }} 
                                                className="cursor-zoomIn" 
                                            />
                                        </div>
                                    ) : (
                                        <ImagesScrollContainer className="imgCollection">
                                            {allQuestionImages.map((imageUrl, imgIndex) => (
                                                <ImageItem key={imgIndex} className="imgCollectionItem">
                                                    <PictureImages 
                                                        src={imageUrl} 
                                                        onClick={() => {
                                                            setSelectedQuestionImage(imageUrl);
                                                            setQuestionImagePreviewModal(true);
                                                        }} 
                                                        className="cursor-zoomIn" 
                                                    />
                                                </ImageItem>
                                            ))}
                                        </ImagesScrollContainer>
                                    )}
                                </>
                            )}
                        </Question>
                    </>

                )
            case 'video':
                return (
                    <div>
                        <Question className="breakTheSentence">
                            Question {index + 1}.
                        </Question>
                        <div>
                            {question.video ? (
                                <>
                                <video   
                                    playsinline
                                    webkit-playsinline
                                    preload="metadata" 
                                    style={{ marginLeft: 12, width: '55%' }} 
                                    controls 
                                    controlsList="nodownload" 
                                    src={question.video} 
                                />
                                </>
                            ) : <span dangerouslySetInnerHTML={{ __html: question.question || '' }} />}
                            {/* {question.video ? <video dangerouslySetInnerHTML={{ __html: question.video || '' }} /> : <span dangerouslySetInnerHTML={{ __html: question.question || '' }} />} */}
                            {/* Standard */}
                        {renderStandardWithInfo()}
                        </div>
                    </div>
                );
            case "filling":
            case "fillingImage":
                return (
                  <>
                   <Question className="breakTheSentence" style={{paddingInline: "4px", alignItems:"flex-start"}}>
                          <label style={{textWrap: "nowrap"}} >{index + 1}.</label><span className="fillingQues" dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                          {/* Standard */}
                        {renderStandardWithInfo()}
                    </Question>
                  </>
                );
                case "photoCollage":
                    return (
                        <Question className="breakTheSentence" style={{paddingInline: "4px", alignItems:"flex-start"}}>
                          <label style={{display:"flex", whiteSpace: "nowrap"}}>{index + 1}.</label><span dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                        </Question>
                    )
               
                default:
                return null;
        }
    }, [question, index, standard, isStudent, renderStandardWithInfo]);
    const ViewQuestionImage = ({ viewimage, type }) => {
        // const q_type = getType(type);
        if (isURL(question.image_url)) {
            return <ImageQuestionArea>
                <Question className="breakTheSentence" style={{ justifyContent: "flex-start" , alignItems: "flex-start"}}><label>{index + 1}.</label> <span dangerouslySetInnerHTML={{ __html: question.question || '' }} /></Question>
                {/* {q_type ? <Typography variant="subtitle2" >Type:  {q_type}</Typography> : null} */}
                {viewimage && <QImage src={question.image_url} />}
            </ImageQuestionArea>
        } else {
            return (<>
                <Question className="breakTheSentence" style={{ justifyContent: "flex-start" , alignItems: "flex-start"}}><label>{index + 1}.</label> <span dangerouslySetInnerHTML={{ __html: question.question || '' }} /></Question>
                {/* {q_type ? <Typography variant="subtitle2" >Type:  {q_type}</Typography> : null} */}
            </>)
        }
    }
    const gameQuestionContent = useMemo(() => {
        if (!question) return null;
        switch (question.type) {
            case 'true_or_false':
            case 'brainstorm':
            case 'drop_pin':
            case 'type_answer':
            case 'poll':
            case 'quiz':
            case 'puzzle':
            case 'word_cloud':
            case 'open_ended':
                return <ViewQuestionImage viewimage={question.type !== 'drop_pin'} type={question.type} />;
            default:
                return null;
        }
    }, [question, index]);

    const checkOptionCorrect = (option,correctAnswer) => {
        if(correctAnswer?.includes(option)){
            return true;
        }
        return false;
    }

    const answerContent = useMemo(() => {
        const neutral = Boolean(not_show_indication);
        if (!question || !answer) {
            return (
                <ImageAnswerArea>
                         <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                         <Answer correct>__</Answer>
                </ImageAnswerArea>
            )
        } 

        const ans = get(answer, "answer");
        const qAnswer = get(question, "answer");
        const imageAnswerUrl = get(answer, "imageUrl")
        const additionalAnswerImages = get(answer, "additionalImageUrls") || get(answer, "additionalImages") || [];
        
        const allAnswerImages = [];
        if (imageAnswerUrl) {
            if (Array.isArray(imageAnswerUrl)) {
                allAnswerImages.push(...imageAnswerUrl);
            } else {
                allAnswerImages.push(imageAnswerUrl);
            }
        }
        if (Array.isArray(additionalAnswerImages) && additionalAnswerImages.length > 0) {
            allAnswerImages.push(...additionalAnswerImages.filter(img => img));
        }

        switch (question.type) {
            case "multipleChoice":
                return (
                    <>
                    <ImageAnswerArea>
                     <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        <Answer $neutral={neutral} correct={answer?.isStudentHaveDiff ? answer?.correct_answers == answer?.answer : qAnswer == ans}>
                           {answer?.isStudentHaveDiff && answer?.studentDiffOption ? answer?.studentDiffOption[ans]?.value : question.options[ans]}
                        </Answer>
                        {!isStudent && !answer?.is_correct && (
                            <>
                           <Answer correct={true}>
                                {answer?.studentDiffOption ? answer?.studentDiffOption[Number(answer?.correct_answers)]?.value || "-" : question.options[Number(qAnswer)]}
                            </Answer>
                            </>
                        )}
                        </ImageAnswerArea>
                    </>
                );
            case "trueFalse":
            case "audioTrueFalse":
            case "audioMultipleChoice":
                return (
                    <>
                     <ImageAnswerArea>
                     <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        <Answer $neutral={neutral} correct={qAnswer == ans}>
                            {question.options[ans]}
                        </Answer>
                        {!isStudent && qAnswer != ans && (
                            <Answer correct>
                                {question.options[qAnswer]}
                            </Answer>
                        )}
                        </ImageAnswerArea>
                    </>
                );
            case "multipleResponse":
                return (
                    <>
                     <ImageAnswerArea>
                     <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                     
                     {(() => {
                        const correctAnswersLength = answer?.correct_answers?.length;
                        const studentAnswersLength = answer?.answer?.length;
                        const optionCountMessage = studentAnswersLength < correctAnswersLength 
                            ? "You have selected less than the expected number of options"
                            : studentAnswersLength > correctAnswersLength 
                            ? "You have selected more than the expected number of options"
                            : null;
                        if(answer?.isStudentHaveDiff){
                            return (<>
                            {answer?.answer?.map((element, index) => {
                                return (
                                <>    
                                <Answer key={index} $neutral={neutral} correct={checkOptionCorrect(element,answer?.correct_answers)}>
                                    {question.options[element] || question.options[index]}
                                </Answer>
                                </>)
                            })}
                            {!neutral && isStudent && !answer?.is_correct && optionCountMessage && (
                                <NoteMark note={optionCountMessage} />
                            )}
                            {!isStudent && answer?.is_correct && (
                                    <>
                                        <NoteMark note={"Due to differentiation, the student receives full marks for this answer."} />
                                    </>
                                )}
                                {!isStudent && (
                                     <p style={{fontSize: "14px", color: "gray", fontWeight: "600"}}>
                                     Correct Answer: {answer?.correct_answers?.map((element,index) => <div key={index} style={{fontSize: "14px"}} className="text-green-700 text-base">{question.options[element] || question.options[index]}</div>)}
                                  </p>

                                )}
                                 
                            </>)
                        }else{
                            return (<>
                            {answer?.answer?.map((element, index) => {
                                return (
                                <Answer key={index} $neutral={neutral} correct={checkOptionCorrect(element,answer?.correct_answers)}>
                                    {question.options[element] || question.options[index]}
                                </Answer>)
                            })}
                            {!neutral && isStudent && !answer?.is_correct && optionCountMessage && (
                                <NoteMark note={optionCountMessage} />
                            )}
                            {!isStudent && (
                                <p style={{fontSize: "12px", color: "gray", fontWeight: "600"}}>
                                    Correct Answer: {answer?.correct_answers?.map((element,index) => <div key={index} style={{fontSize: "14px"}} className="text-green-700 text-base">{question.options[element] || question.options[index]}</div>)}
                                </p>
                            )}
                            </>)
                        }
                     })()}
                        </ImageAnswerArea>
                    </>
                );
            case "essay":
            case "audioEssay":
            case "email":
                return (
                    <>
                    <ImageAnswerArea>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        <MessageRenderer style={{ color: 'rgb(0, 0, 0)' }} data={ans} />
                        <AIGradingAndFeedback ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} question={question} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                    </ImageAnswerArea>
                        
                    </>
                );
            case "audio":
            case "twoMinAudio":
                return ans ? (
                    <ImageAnswerArea>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                    {(() => {
                        const audioKey = `answer-${index}-${ans}`;
                        if (!audioRefs.current.has(audioKey)) {
                            audioRefs.current.set(audioKey, React.createRef());
                        }
                        const audioRef = audioRefs.current.get(audioKey);
                        return (
                            <TranscriptWithAudio audioUrl={ans} title={question.question}>
                                <audio 
                                    ref={audioRef}
                                    style={{ marginLeft: 12 }} 
                                    controls 
                                    controlsList={ "nodownload"} 
                                    src={ans}
                                    onPlay={() => pauseAllOtherAudios(audioKey)}
                                />
                            </TranscriptWithAudio>
                        );
                    })()}
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} isSuggestion={true} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                    </ImageAnswerArea>    
                ) : null;
            case 'match':
                return (
                    <>
                        {!isStudent && (
                            <>
                            <Question className="breakTheSentence">
                                Answer Key:
                            </Question>
                            <div style={{ display: 'flex', flexWrap:"wrap" }}>
                                {map(get(question, "options", [""]), (option, optId) => (
                                    <Question className="breakTheSentence" style={{ paddingLeft: '10px', paddingRight: '10px', minWidth:"0%" }} index={(optId)}>
                                        <span className="matchItemQuestion" dangerouslySetInnerHTML={{ __html: option?.item || '' }} />
                                        <div
                                            style={{
                                                borderRadius: "0px !important",
                                                minWidth: "20px",
                                                maxHeight: '20px',
                                                color: 'white',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                marginLeft: '5px',
                                            }}
                                            className={`matchItemIndex`}
                                        >
                                            {String.fromCharCode(65 + optId)}
                                        </div>
                                    </Question>
                                ))}
                            </div>
                            </>
                        )}
                        <ImageAnswerArea>
                            <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                            <MatchMakingReview question={question} answer={answer} neutral={neutral} />
                        </ImageAnswerArea>
                    </>
                )
            case "video":
                return ans ? (
                    <ImageAnswerArea>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                    <video   playsinline
                             webkit-playsinline
                            preload="metadata" style={{ marginLeft: 12, width: '55%' }} controls controlsList="nodownload" src={ans} />
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} isSuggestion={true} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                    </ImageAnswerArea>    
                ) : null;
            case "audioToImage":
                return (
                    <>
                     <ImageAnswerArea style={{marginTop: "40px"}}>
                     <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        {allAnswerImages.length > 0 && (
                            <>
                                {allAnswerImages.length === 1 ? (
                                        <PictureImages 
                                            className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                            onClick={() => {
                                                setSelectedAnswerImage(allAnswerImages[0]);
                                                setImagePreviewModal(true);
                                            }} 
                                            src={allAnswerImages[0]} 
                                        />
                                ) : (
                                        <ImagesScrollContainer className="imgCollection">
                                            {allAnswerImages.map((imageUrl, imgIndex) => (
                                                <ImageItem key={imgIndex} className="imgCollectionItem">
                                                    <PictureImages 
                                                        className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                                        onClick={() => {
                                                            setSelectedAnswerImage(imageUrl);
                                                            setImagePreviewModal(true);
                                                        }} 
                                                        src={imageUrl} 
                                                    />
                                                </ImageItem>
                                            ))}
                                        </ImagesScrollContainer>
                                )}
                            </>
                        )}
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                        </ImageAnswerArea>
                    </>
                );
                case "picture":
                if(question?.categoryType === "Image"){                    
                    return   (
                    <>
                     <ImageAnswerArea style={{marginTop: "40px"}}>
                         <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        {allAnswerImages.length > 0 && (
                            <>
                                {allAnswerImages.length === 1 ? (
                                        <PictureImages 
                                            className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                            onClick={() => {
                                                setSelectedAnswerImage(allAnswerImages[0]);
                                                setImagePreviewModal(true);
                                            }} 
                                            src={allAnswerImages[0]} 
                                        />
                                ) : (
                                        <ImagesScrollContainer className="imgCollection">
                                            {allAnswerImages.map((imageUrl, imgIndex) => (
                                                <ImageItem key={imgIndex} className="imgCollectionItem">
                                                    <PictureImages 
                                                        className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                                        onClick={() => {
                                                            setSelectedAnswerImage(imageUrl);
                                                            setImagePreviewModal(true);
                                                        }} 
                                                        src={imageUrl} 
                                                    />
                                                </ImageItem>
                                            ))}
                                        </ImagesScrollContainer>
                                )}
                            </>
                        )}
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                     </ImageAnswerArea>
                  
                    </>
                    )
                }
                else if(question?.categoryType === "Text"){
                   return (
                   <>
                     <ImageAnswerArea style={{marginTop: "40px"}}>
                     <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        <MessageRenderer style={{ color: 'rgb(0, 0, 0)' }} data={ans} />
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                        </ImageAnswerArea>
                    </>
                   )
                }
                else if(question?.categoryType === "ImageAndText"){
                return (
                    <>
                    <ImageAnswerArea style={{marginTop: "40px"}}>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                        {allAnswerImages.length > 0 && (
                            <>
                                {allAnswerImages.length === 1 ? (
                                        <PictureImages  
                                            className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                            src={allAnswerImages[0]}  
                                            onClick={() => {
                                                setSelectedAnswerImage(allAnswerImages[0]);
                                                setImagePreviewModal(true);
                                            }} 
                                        />
                                ) : (
                                        <ImagesScrollContainer className="imgCollection">
                                            {allAnswerImages.map((imageUrl, imgIndex) => (
                                                <ImageItem key={imgIndex} className="imgCollectionItem">
                                                    <PictureImages  
                                                        className="cursor-zoomIn gradeBook-answer-image-width-height" 
                                                        src={imageUrl}  
                                                        onClick={() => {
                                                            setSelectedAnswerImage(imageUrl);
                                                            setImagePreviewModal(true);
                                                        }} 
                                                    />
                                                </ImageItem>
                                            ))}
                                        </ImagesScrollContainer>
                                )}
                            </>
                        )}
                        <MessageRenderer style={{ color: 'rgb(0, 0, 0)' }} data={ans} />
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                        </ImageAnswerArea>
                   
                    </>
                )
                }
                 else if(question?.categoryType === "Audio"){
                return (
                    <>
                    <ImageAnswerArea style={{marginTop: "40px"}}>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                    {(() => {
                        const audioKey = `answer-${index}-${ans}`;
                        if (!audioRefs.current.has(audioKey)) {
                            audioRefs.current.set(audioKey, React.createRef());
                        }
                        const audioRef = audioRefs.current.get(audioKey);
                        return (
                            <TranscriptWithAudio audioUrl={ans} title={question.question}>
                                <audio 
                                    ref={audioRef}
                                    style={{ marginLeft: 12 }} 
                                    controls 
                                    controlsList={"nodownload"} 
                                    src={ans}
                                    onPlay={() => pauseAllOtherAudios(audioKey)}
                                />
                            </TranscriptWithAudio>
                        );
                    })()}
                        <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                    </ImageAnswerArea>
                   
                    </>
                )
                }

                case "filling":
            case "fillingImage":
                return (
                    <>
                    <GradeDisplayPreview isStudent={isStudent} question={question} index={index} answer={answer} neutral={neutral} />
                    </>
                );
            case "photoCollage":
                return (
                  <>
                    <ImageAnswerArea>
                      <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>

                      <AssignmentPhotoCollage
                        states={{
                          frameCollage: answer?.answer,
                          isPreview: true,
                          gradePreview: true,
                        }}
                        funcObj={{}}
                      />
                      {question?.type_question_desc && question?.type_question_desc?.length > 0 && (
                        <div className="mt-10px mb-10px">
                          Description:-{question?.type_question_desc}{' '}
                        </div>
                      )}
                      {question?.categoryType?.includes('Text') && (
                        <>
                          <MessageRenderer
                            style={{ color: 'rgb(0, 0, 0)' }}
                            data={answer?.answer_text}
                          />
                        </>
                      )}
                      {question?.categoryType?.includes('Audio') && (() => {
                        const audioKey = `answer-${index}-${answer?.answer_audio}`;
                        if (!audioRefs.current.has(audioKey)) {
                            audioRefs.current.set(audioKey, React.createRef());
                        }
                        const audioRef = audioRefs.current.get(audioKey);
                        return (
                            <>
                                <TranscriptWithAudio audioUrl={answer?.answer_audio} title={question.question}>
                                    <audio
                                        ref={audioRef}
                                        style={{ marginLeft: 12 }}
                                        controls
                                        controlsList={"nodownload"}
                                        src={answer?.answer_audio}
                                        onPlay={() => pauseAllOtherAudios(audioKey)}
                                    />
                                </TranscriptWithAudio>
                            </>
                        );
                      })()}
                      <AIGradingAndFeedback question={question} ai_grade={ai_grade} ai_feedback={aiFeedback} ai_suggestion={aiSuggestion} languageData={languageData} isFeedBack={isFeedBack} rubricsToggle={rubricsToggle} isCompleted={isCompleted} />
                    </ImageAnswerArea>
                  </>
                );
            default:
                return null;
        }
    }, [answer, question, isStudent, aiFeedback, aiSuggestion, not_show_indication, rubricsToggle]);
    const ViewAnswerImage = ({ ans, isCorrect, qAnswer, type }) => {
        const is_poll = type === 'poll'
        const show_answer = !isStudent && !isCorrect && !is_poll
        const border_color = not_show_indication ? 'transparent' : ((isCorrect || is_poll) ? COLOR.green100 : COLOR.red)

        const isAnswerImage = isURL(ans);
        const isCorrectAnswerImage = isURL(qAnswer);

        return (<ImageAnswerArea>
            <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
            {isAnswerImage ? (
                <QImage src={ans} style={{ border: `2px solid ${border_color}` }} />
            ) : (
                <Answer $neutral={not_show_indication} correct={isCorrect || is_poll}>
                    {ans}
                </Answer>
            )}
            {show_answer && (
                isCorrectAnswerImage ? (
                    <QImage src={qAnswer} style={{ border: `2px solid ${COLOR.green100}` }} />
                ) : (
                    <Answer correct>
                        {type === 'type_answer' && Array.isArray(qAnswer) ? qAnswer.join(',') : qAnswer}
                    </Answer>
                )
            )}
        </ImageAnswerArea>)
    }
    const ViewPinPoint = ({ image_url, pointer_poistion }) => {
        const { x: xpoint, y: ypoint } = pointer_poistion || {}
        return (<ImageAnswerArea>
            <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
            <PointerContainer bg={image_url}>
                <PinPointWrapper x={xpoint} y={ypoint} >
                    <PinPoint />
                </PinPointWrapper>
            </PointerContainer>
        </ImageAnswerArea>)
    }
    const onPreview = () => { setOpen(true); }
    const ViewGraphicOrganizer = ({  }) => {
        const input_position = activity.input_position || []
        const label_position = activity.label_position || []
        // const allLocations = [...label_position, ...input_position]
        return (
        <ImageAnswerArea>
            <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
            <div>
            <MainGraphicViewBtn onClick={onPreview} style={{textAlign:"right", marginTop: "3px", marginRight: "3px"}}>
                <GraphicViewBtn>
                    Click here for the full view
                </GraphicViewBtn>
            </MainGraphicViewBtn>
            <GraphicContainer bg={page?.label_position?.length > 0 ? page?.edit_image_src : page?.resource} onClick={onPreview}>
                {input_position.map((positions, i) => {
                    const ans  = Array.isArray(answer) ? answer[i] : {};
                    return (
                        <div className={`${positions.class_name} scrollViewGrad`} style={{
                            position: 'absolute',
                            width: `calc(${positions.width} * 100%)`,
                            height: positions.height ? `calc(${positions.height} * 100%)` : '',
                            left: `calc(${positions.left} * 100%)`,
                            top: `calc(${positions.top} * 100%)`,
                            fontSize:12,
                            // display: "flex",
                            // textWrap:"wrap",
                            // overflow: "hidden",
                            // maxHeight: "100px",
                            wordWrap:"break-word",
                            overflowY:"auto",
                            padding:"5px"
                        }}>
                           {
                            ans?.type === "text"
                                ? ans.answer
                                : ans?.answer
                                ? (
                                    <img
                                        src={ans.answer}
                                        style={{
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                        objectFit: "contain",
                                        }}
                                    />
                                    )
                                : null
                            }

                        </div>
                    )
                })}
                {label_position.map((positions, i) => {
                    // const ans  = Array.isArray(answer) ? answer[i] : { value: ''};
                    return (
                        <div className={`${positions.class_name} scrollViewGrad`} style={{
                            position: 'absolute',
                            width: `calc(${positions.width} * 100%)`,
                            height: positions.height ? `calc(${positions.height} * 100%)` : '',
                            left: `calc(${positions.left} * 100%)`,
                            top: `calc(${positions.top} * 100%)`,
                            fontSize:12,
                            // display: "flex",
                            // textWrap:"wrap",
                            // overflow: "hidden",
                            // maxHeight: "100px",
                            wordWrap:"break-word",
                            overflowY:"auto",
                            padding:"5px",
                            zIndex: 1000
                        }}>
                            {
                                positions.value
                            }
                        </div>
                    )
                })}
            </GraphicContainer>
            </div>
        </ImageAnswerArea>)
    }
    const ViewPuzzleAnswer = ({ given_answer, correct_answer }) => {
        if (!Array.isArray(given_answer) || !Array.isArray(correct_answer)) {
            return <></>
        }
        const isCorrect = isEqual(given_answer, correct_answer)
        return <ImageAnswerArea>
            <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
            <PuzzleWrapper $neutral={not_show_indication} is_correct={isCorrect}>
                {given_answer.map(v => {
                    const lable_txt_index = findIndex(question.options, k => k == v);
                    const lable_text = lable_txt_index >= 0 ? `(${alphaRef[lable_txt_index]}) - ` : '';
                    return (<PuzzleRow> {lable_text} {" "} {v}</PuzzleRow>)
                })}
            </PuzzleWrapper>
            {!isCorrect && !isStudent ? <PuzzleWrapper is_correct style={{ marginTop: 20 }}>
                {correct_answer.map(v => {
                    const lable_txt_index = findIndex(question.options, k => k == v);
                    const lable_text = lable_txt_index >= 0 ? `(${alphaRef[lable_txt_index]}) - ` : '';
                    return (<PuzzleRow> {lable_text} {" "} {v}</PuzzleRow>)
                })}
            </PuzzleWrapper> : null}
        </ImageAnswerArea>
    }
    const gameAnswerContent = useMemo(() => {
        if (!question || !answer) return null;

        const ans = get(answer, "answer");
        const q_ans = get(question, "answer")

        let qAnswer;
        if (question.type === "type_answer") {
            qAnswer = Array.isArray(q_ans) ? q_ans : q_ans;
        } else {
            qAnswer = Array.isArray(q_ans) && (question.type === "true_or_false" || question.type === "quiz") ? q_ans[0] : q_ans;
        }
        
        const displayAnswer = (!ans || ans === '' || ans === null || ans === undefined) ? '__' : ans;
        
        // Special handling for type_answer to check all correct answers
        let isCorrect = qAnswer == ans;
        if (question.type === "type_answer" && Array.isArray(q_ans)) {
            isCorrect = answer.is_correct;
        }

        switch (question.type) {
            case 'true_or_false':
            case 'quiz':
            case 'type_answer':
            case 'poll':
                return (
                    <ViewAnswerImage ans={displayAnswer} isCorrect={isCorrect} qAnswer={qAnswer} type={question.type} />
                );
            case 'open_ended':
            case 'word_cloud':
            case 'brainstorm':
                return (<ImageAnswerArea>
                    <AnswerMarkLagendaArea>Answer</AnswerMarkLagendaArea>
                    <Answer correct>
                        {displayAnswer}
                    </Answer>
                </ImageAnswerArea>
                )
            case 'drop_pin':
                return <ViewPinPoint image_url={question.image_url} pointer_poistion={ans} />
            case 'puzzle':
                return (<ViewPuzzleAnswer given_answer={ans} correct_answer={q_ans} />)
            default:
                return null;
        }
    }, [answer, question, isStudent, aiFeedback]);
    if (is_game) {
        return (
            <Subsection>
                {gameQuestionContent}
                {gameAnswerContent}
            </Subsection>
        );
    } else if (is_debate) {
        return (
            <Subsection>
                {<Question className="breakTheSentence">{question.question}</Question>}
                {map(sortSubmissionsByUserId(answer, selected_student_id), (ans, i) => (<ImageAnswerArea key={i}>
                    <AnswerByUserDebate active={selected_student_id === ans.user_id} style={{ fontSize: 14 }}>
                        {ans.user_profile_img ? <IconImg src={getAvatar(ans.user_profile_img)} /> : <UserOutlined size={"small"} style={{ marginRight: 4 }} />}
                        <b>{ans.user_name}</b>:-<div className="debateAnswer"> <span dangerouslySetInnerHTML={{ __html: ans.answer || '' }} />  </div> </AnswerByUserDebate>
                    <SubComment>
                        {map(ans.comment, cmnt => (<AnswerByUserDebate active={selected_student_id === cmnt.user_id} key={i + cmnt.user_id + "rep"} style={{ fontSize: 13 }}>
                            {cmnt.user_profile_img ? <CommentIconImg src={getAvatar(cmnt.user_profile_img)} /> : <UserOutlined size={"small"} style={{ marginRight: 4 }} />}
                            <b>{cmnt.user_name}</b>:- {cmnt.reply_text}</AnswerByUserDebate>))}
                    </SubComment>
                </ImageAnswerArea>))}
            </Subsection>
        );
    } else if (is_loteria) {
        return (
            <Subsection>
                {<Question>{question.question}</Question>}
                {map(sortSubmissionsByUserId(answer, selected_student_id), (ans, i) => (
                    <ImageAnswerArea key={i}>
                        <AnswerByUserDebate active={selected_student_id === ans.user_id} style={{ fontSize: 14 }}>
                            <GridView grid={transformToGrid(JSON.parse(ans.answer))} readOnly={true} selectedType={ans.bingo_type} wheelSpinData={ans.wheelSpinResult} from="studentDataView" />
                        </AnswerByUserDebate>
                    </ImageAnswerArea>
                ))}
            </Subsection>
        );
    } else if(is_graphic_organizer){
        return (
            <Subsection>
                <Modal
                    open={open}
                    onClose={() => setOpen(false)}
                    className={"lessonPlanPopUp"}
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: "transparent",
                        zIndex:999999999999
                    }}
                >
                    <PlanWrapper>
                        <PlanContainer>
                            <CloseBtn
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpen(false);
                                }}
                            >
                                <Close
                                    style={{ color: "#fff", fontWeight: "bolder", fontSize: 25 }}
                                />
                            </CloseBtn>

                            <div
                                style={{position: 'absolute',top: '10px',right: '50px',display: 'flex',flexDirection: 'row',gap: '10px',zIndex: 1}}
                            >
                                <Button 
                                    onClick={increaseFontSize}
                                    style={{width: '30px',height: '30px',padding: '8px',backgroundColor: '#1890ff',color: COLOR.white,borderRadius:'100%',display: 'flex',alignItems: 'center',justifyContent: 'center'}}
                                >
                                    <TextIncreaseIcon style={{fontSize: '20px'}} onClick={increaseFontSize} />
                                </Button>
                                
                                <Button 
                                    onClick={decreaseFontSize}
                                    style={{width: '30px',height: '30px',padding: '8px',backgroundColor: '#1890ff',color: COLOR.white,borderRadius:'100%',display: 'flex',alignItems: 'center',justifyContent: 'center'}}
                                >
                                    <TextDecreaseIcon style={{fontSize: '20px'}} onClick={decreaseFontSize} />
                                </Button>
                            </div>
                            <Container>
                                <PrintWrapper>
                                    <AnswerWindow src={ page?.label_position?.length > 0 ? page?.edit_image_src : page?.resource} initialFontSize={initialFontSize} setInitialFontSize={setInitialFontSize} input_position={activity.input_position || []} label_position={activity.label_position || []} answers={answer} setImageModalState={setImageModalState} />
                                </PrintWrapper>
                            </Container>
                        </PlanContainer>
                    </PlanWrapper>
                </Modal>
                <ViewGraphicOrganizer />
            </Subsection>
        );
    } else if(is_worksheet){
         return <QuestionPreview question={question} answer={answer} q_id={index+1} />;
    }
    else{
        return (
            <Subsection>
                {questionContent}
                {question.standard_story && (
                    <div>
                        <StandardStoryHeading className="breakTheSentence">
                            Standard Story:
                        </StandardStoryHeading>
                        <StandardStoryContent className="breakTheSentence">
                            {question.standard_story}
                        </StandardStoryContent>
                    </div>
                )}
                {answerContent}
                {question?.type === 'picture' && questionImagePreviewModal && selectedQuestionImage && (
                    <ModalMenu 
                        type="imageModal" 
                        image={selectedQuestionImage} 
                        open={questionImagePreviewModal} 
                        close={() => {
                            setQuestionImagePreviewModal(false);
                            setSelectedQuestionImage(null);
                        }} 
                    />
                )}
                {(question?.type === 'picture' || question?.type === 'audioToImage') && imagePreviewModal && selectedAnswerImage && (
                    <ModalMenu 
                        type="imageModal" 
                        image={selectedAnswerImage} 
                        open={imagePreviewModal} 
                        close={() => {
                            setImagePreviewModal(false);
                            setSelectedAnswerImage(null);
                        }} 
                    />
                )}
                <StandardsModal
                    open={standardsModal.open}
                    onClose={() => setStandardsModal({ open: false, standardAbbrs: [], stateStandard: null })}
                    standardAbbrs={standardsModal.standardAbbrs}
                    stateStandard={standardsModal.stateStandard}
                    standards={standards}
                    enabledStateCodes={enabledStateCodes}
                    userRole={currentUser?.role}
                />
            </Subsection>
        );
    }
};

const GradeDisplay = ({
    assignment,
    answers,
    feedback,
    audioFeedback,
    grade,
    name,
    questions,
    type,
    isExpended,
    isLate,
    hasFeedback = true,
    isUngraded,
    ai_feedback,
    ai_suggestion,
    is_game,
    is_debate,
    selected_student_id,
    rubricsToggle,
    show_ai_grade_button,
    is_loteria,
    isFeedBack = false,
    activity,
    is_worksheet,
    showReturnButton = false,
    isReturnedFromSession = false,
    onReturnSuccess,
    currentSubmissionNo,
    in_progress = false,
    showStudentTrackingButton = false,
    onStudentTrackingClick,
}) => {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(isExpended || false);
    const [returnUiAfterSuccess, setReturnUiAfterSuccess] = useState(false);
    const [returnLoading, setReturnLoading] = useState(false);
    useEffect(() => {
        setReturnUiAfterSuccess(false);
    }, [assignment?.id, assignment?.submissions, assignment?.returned, currentSubmissionNo]);
    const activities = useActivities()
    const classActivities = useClassActivities()
    const classQuizzes = useClassQuizzes()
    const allAssignment = [...classActivities, ...classQuizzes];
    const currentUser = useCurrentUser()
    const standards = useStandards();
    const isStudent = get(currentUser, "role") === "student"
    const isParent = get(currentUser, "role") === "parent"
    const answersLength = get(answers, "length", 0);
    const audioRefs = useRef(new Map());
    const { getMarkStatus, getMarkStatusBingo, hasValidAiFeedback } = useGradeBook();
        
    const pauseAllOtherAudios = (currentAudioKey) => {
        audioRefs.current.forEach((audioRef, key) => {
            if (key !== currentAudioKey && audioRef?.current) {
                try {
                    if (!audioRef.current.paused) {
                        audioRef.current.pause();
                    }
                } catch (error) {
                }
            }
        });
    };
    const activites = useMemo(()=> find(allAssignment, v=>v?.data.id==assignment?.assignmentId||v?.data?.id == assignment?.assignment?.id)?.data, [activities,assignment, allAssignment]);
    const showAlert = useAlert();
    const { createCollectionNotification } = useCollectionNotification();
    const classIdForNotification = useMemo(() => {
        const classAct = find(allAssignment, v => v?.data?.id == assignment?.assignmentId || v?.data?.id == assignment?.assignment?.id);
        return classAct?.classId || "";
    }, [allAssignment, assignment]);
    const sendNotifications = useCallback(async (notificationData) => {
        try {
            createCollectionNotification(notificationData, classIdForNotification);
        } catch (error) {
            Sentry.captureException(`GradeDisplay sendNotifications: ${error}`);
        }
    }, [createCollectionNotification, classIdForNotification]);
    const isDueDatePassed = useCallback(() => {
        const dueDate = activity?.lateDate || activity?.dueDate || activites?.lateDate || activites?.dueDate;
        return dueDate ? isBeforTime(dueDate) : false;
    }, [activity, activites]);
    const returnNotification = useCallback((submission) => {
        const notificationData = {
            id: v4(),
            classId: classIdForNotification,
            displayName: get(submission, "assignment.displayName", ""),
            senderId: get(currentUser, "id", ""),
            receiversId: [].concat(get(submission, "userId", "")),
            modified: moment().toISOString(),
            data: {
                chapter: get(submission, "assignment.chapter", ""),
                page: get(submission, "assignment.page", ""),
                id: get(submission, "assignment.id", ""),
            },
            message: "Your Teacher Returned this Assignment",
            type: "update_quiz",
            userSeen: [],
        };
        sendNotifications(notificationData);
    }, [classIdForNotification, currentUser, sendNotifications]);
    const returnAssignment = useCallback(async () => {
        if (assignment && has(assignment, "id")) {
            const submissionNo = currentSubmissionNo ?? assignment?.submissions;
            const serverReturnedLocal = assignment?.returned > 0 && submissionNo === (assignment?.returned - 1);
            if (serverReturnedLocal || returnUiAfterSuccess || isReturnedFromSession) {
                showAlert("Assignment already returned!", "error");
                return;
            }
            if (isDueDatePassed()) {
                showAlert("You can't return this assignment because it is already past due. Please change the due date if you'd like to return the submission.", "error");
                return;
            }
            if (!assignment.complete) {
                showAlert("You can't return this assignment because it is in progress.", "error");
                return;
            }
            setReturnLoading(true);
            try {
                const result = await dispatch(handleReturnSubmission(assignment.id));
                if (result && result.id) {
                    setReturnUiAfterSuccess(true);
                    onReturnSuccess?.(assignment.id, currentSubmissionNo ?? assignment?.submissions);
                    if (classIdForNotification) {
                        await dispatch(handleListClassSubmissions(classIdForNotification));
                    }
                    showAlert("Assignment returned successfully.", "success");
                    returnNotification(assignment);
                } else {
                    showAlert("Having issue while returning Assignment", "error");
                }
            } finally {
                setReturnLoading(false);
            }
        } else {
            showAlert("Assignment is not submitted by student.", "error");
        }
    }, [assignment, dispatch, isDueDatePassed, returnNotification, showAlert, onReturnSuccess, returnUiAfterSuccess, isReturnedFromSession]);
    const graphic_page = useMemo(() => {
        let dt = find(graphic_data, k => k.pageId == activites?.assignment_page);
        if (dt) return ({ ...dt, resource: dt.src })
        return null
    }, [activites]);
    const is_graphic_organizer =  useMemo(()=>activites?.activity_type === "graphic_organizer",[activites])
    const [imagemodalOpen, setImageModalState] = useState({ open: false, src: '' })
    const languageData = useMemo(() => {
        const assignment_feedback = find(activities, (obj) => obj.id == assignment?.assignmentId)?.language_feedback || 'SPANISH'
        const class_act = find(allAssignment, v => v?.data.id == assignment?.assignmentId || v?.data?.id == assignment?.assignment?.id)||{};
        
        if (selected_student_id && class_act.classId) {
            const assignmentData = class_act.data || class_act;
            const groupConfig = getDiffConfigForStudent(selected_student_id, assignmentData, class_act.classId);
            if (groupConfig?.ai_grading_config?.language_feedback) {
                return groupConfig.ai_grading_config.language_feedback;
            }
        }
        
        const ai_grading_config = class_act.data?.ai_grading_config || class_act.ai_grading_config || { creteria: {} };
        const lang_feedback = ai_grading_config.creteria?.language_feedback;
        return lang_feedback || assignment_feedback || 'SPANISH';
    }, [activites, allAssignment, selected_student_id, assignment]);
    const aiGradingLevel = useMemo(() => {
        const isUuid = (value) => (
            typeof value === 'string' &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
        );
        const latestSubmissionIndex = Math.max((assignment?.submissions || 1) - 1, 0);
        const submissionNo = currentSubmissionNo ?? latestSubmissionIndex;
        const isCurrentSubmissionView = Boolean(showReturnButton) || submissionNo === latestSubmissionIndex;
        let aiGradeLevel = null;
        let configGradeLevel = null;
        if (assignment?.ai_grade && Array.isArray(assignment.ai_grade) && assignment.ai_grade.length > 0) {
            const firstAiGrade = assignment.ai_grade[0];
            if (firstAiGrade?.grade_level) {
                aiGradeLevel = firstAiGrade.grade_level;
            }
        }
        const submissionAiConfig = assignment?.ai_grading_config || {};
        const submissionConfigGradeLevel =
            submissionAiConfig?.creteria?.grade_level ||
            submissionAiConfig?.criteria?.grade_level ||
            submissionAiConfig?.grade_level ||
            null;
        if (submissionConfigGradeLevel) {
            configGradeLevel = submissionConfigGradeLevel;
        }
        const class_act = find(allAssignment, v => v?.data.id == assignment?.assignmentId || v?.data?.id == assignment?.assignment?.id) || {};
        if (!configGradeLevel && (!assignment?.complete || isCurrentSubmissionView) && selected_student_id && class_act.classId) {
            const groupConfig = getDiffConfigForStudent(selected_student_id, class_act, class_act.classId);
            if (groupConfig?.ai_grading_config?.grade_level) {
                configGradeLevel = groupConfig.ai_grading_config.grade_level;
            }
        }
        if (!configGradeLevel && (!assignment?.complete || isCurrentSubmissionView)) {
            const classConfig = class_act.classes?.find(c => c.id === class_act.classId);
            const ai_grading_config = class_act.ai_grading_config || classConfig?.ai_grading_config || class_act.data?.ai_grading_config || { creteria: {} };
            configGradeLevel =
                ai_grading_config?.creteria?.grade_level ||
                ai_grading_config?.criteria?.grade_level ||
                ai_grading_config?.grade_level;
        }
        let grade_level = aiGradeLevel;
        if (!grade_level || isUuid(grade_level)) {
            grade_level = configGradeLevel || grade_level;
        }
        if (!grade_level || isUuid(grade_level)) {
            return null;
        }
        const levelMap = {
            NoviceLow: 'Novice low',
            NoviceMid: 'Novice mid',
            NoviceHigh: 'Novice high',
            IntermediateLow: 'Intermediate low',
            IntermediateMid: 'Intermediate mid',
            IntermediateHigh: 'Intermediate high',
            AdvanceLow: 'Advance low',
            AdvanceMid: 'Advance mid',
            AdvanceHigh: 'Advance high',
        };
        return levelMap[grade_level] || grade_level;
    }, [allAssignment, assignment, selected_student_id, currentSubmissionNo]);
    const ai_grade = useMemo(()=>assignment?assignment.ai_grade:[],[assignment])
    const renderedGroups = new Set();
    const { districtPermissionConfig } = usePermissionForUser();
    const {audio_download= false} = useMemo(() => {
      return districtPermissionConfig
    }, [districtPermissionConfig])
    const waiting_for_ai_grading = useMemo(()=>{
        if(is_graphic_organizer) return false;
        if(Array.isArray(questions)){
            try {
                if(assignment&&assignment.waiting_for_ai_grade) return true;
                else if(ai_grade&&ai_grade.length > 0) return false;
                else return false;
            } catch (error) {
                Sentry.captureException(`210 Error in waiting_for_ai_grading: ${error}`);
                return false;
            }
           
        }
        return false;
    },[questions,ai_grade,assignment,is_graphic_organizer]);
    const is_with_time = useMemo(() => {
        return assignment && assignment.is_with_time && activity?.timeDuration && activity?.timeDuration > 0
    }, [assignment, activity]);

    const not_show_indication = useMemo(() => {
        return Boolean(assignment?.is_current);
    }, [assignment]);

    const renderAnswers = useMemo(() => {
        return questions && questions.map((question, i) => {
            if(i>0) return null;
            return (
                <QuestionComponent
                    key={`${i}-question`}
                    question={question}
                    index={i}
                    answer={is_debate || is_loteria || is_graphic_organizer ? answers : answers[i]}
                    allQuestions={questions}
                    is_game={is_game}
                    is_debate={is_debate}
                    is_loteria={is_loteria}
                    isStudent={isStudent}
                    selected_student_id={selected_student_id}
                    aiFeedback={ai_feedback ? ai_feedback[i] : null}
                    ai_grade= {ai_grade?ai_grade[i]:null}
                    is_graphic_organizer={is_graphic_organizer}
                    activity={activites}
                    page={graphic_page}
                    setImageModalState={setImageModalState}
                    aiSuggestion={ai_suggestion ? ai_suggestion[i] : null}
                    languageData={languageData}
                    isFeedBack={isFeedBack}
                    renderedGroups={renderedGroups}
                    currentUser={currentUser}
                    pauseAllOtherAudios={pauseAllOtherAudios}
                    audioRefs={audioRefs}
                    not_show_indication={not_show_indication}
                    rubricsToggle={rubricsToggle}
                    isCompleted={Boolean(assignment?.complete)}
                />
            )
        })
    }, [is_graphic_organizer, questions, is_game, is_debate, answers, ai_feedback, ai_suggestion, graphic_page, activites, is_loteria, assignment, not_show_indication, rubricsToggle])

    const serverReturned = useMemo(() => {
        const submissionNo = currentSubmissionNo ?? assignment?.submissions;
        return assignment?.returned > 0 && submissionNo === (assignment?.returned - 1);
    }, [assignment?.returned, assignment?.submissions, currentSubmissionNo]);
    const isReturnedState = serverReturned || returnUiAfterSuccess || isReturnedFromSession;

    const validAiFeedback = useMemo(() => {
        return hasValidAiFeedback(assignment);
    }, [assignment]);
       
    return (
        <SentryWrap>
        <Wrapper>
            <Button>
                <Name className="breakTheSentence" isLate={isLate}>{name}</Name>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                    {!validAiFeedback ? <Text>-</Text>: (
                          <Text>{isUngraded ? (isReturnedState ? "Credit" : (assignment?.assignmentType === "bingo" ? getMarkStatusBingo(isUngraded,assignment?.submissions || 0,assignment) : getMarkStatus(isUngraded,assignment?.submissions || 0,assignment))) : grade}</Text>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {!isStudent && !isParent && showStudentTrackingButton && onStudentTrackingClick && (
                            <MtButton
                                type="button"
                                variant="contained"
                                disableElevation
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStudentTrackingClick();
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                startIcon={<AnalyticsOutlined />}
                                sx={{
                                    px: { xs: '8px', sm: '12px' },
                                    py: '4px',
                                    minWidth: 'fit-content',
                                    height: { xs: '30px', sm: '28px' },
                                    fontSize: { xs: '11px', sm: '12px' },
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    textTransform: "none",
                                    color: "#fff",
                                    backgroundColor: "#03989e",
                                    boxShadow: "0 4px 12px rgba(3,152,158,0.35)",
                                    "& .MuiButton-startIcon": {
                                        marginRight: '6px',
                                        marginLeft: 0,
                                    },
                                    "&:hover:not(.Mui-disabled)": {
                                        backgroundColor: "#02796d",
                                    },
                                }}
                            >
                                {WORK_SESSION_VIEW_BUTTON_LABEL}
                            </MtButton>
                        )}
                        {!isStudent && !isParent && showReturnButton && assignment?.complete && !in_progress && (
                            <MtButton
                                type="button"
                                variant="contained"
                                disableElevation
                                disabled={returnLoading}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    returnAssignment();
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                startIcon={returnLoading ? <CircularProgress size={14} color="inherit" sx={{ color: "#fff" }} /> : null}
                                sx={{
                                    padding: '4px 12px',
                                    minWidth: 'auto',
                                    height: '28px',
                                    fontSize: '12px',
                                    textTransform: "none",
                                    color: "#fff",
                                    backgroundColor: "#4a9a9a",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                    "&:hover:not(.Mui-disabled)": {
                                        backgroundColor: "#3d8585",
                                        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
                                    },
                                }}
                            >
                                {returnLoading ? "Loading..." : isReturnedState ? "Returned" : "Return"}
                            </MtButton>
                        )} 
                    </div>
                </div>
            </Button>
            <div className="solicitarLevelGrading">
                    <div>
                    <AiGradingButton
                        assignment={assignment}
                        show_ai_grade_button={show_ai_grade_button}
                        is_graphic_organizer={is_graphic_organizer}
                        answersLength={answersLength}
                        allAssignment={allAssignment}
                        selected_student_id={selected_student_id}
                    />
                    </div>
                    <div>
                    {!isStudent && !isParent && aiGradingLevel && (
                            <AiGradingLevelContainer>
                                <AiGradingLevelLabel>Level for AI grading:</AiGradingLevelLabel>
                                <AiGradingLevelValue style={{ whiteSpace: 'break-spaces' }}>{aiGradingLevel}</AiGradingLevelValue>
                            </AiGradingLevelContainer>
                    )}
                    </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {rubricsToggle}
                </div>
            </div>
            <Modal
                open={imagemodalOpen.open}
                onClose={() => setImageModalState({open: false , src:''})}
                style={{ height: '100%', width: '100%', zIndex: 9999999999999 }}
            >
                <ImageModal>
                    <Image src={imagemodalOpen.src} alt='' />
                </ImageModal>
            </Modal>
            {answersLength === 0 && assignment?.complete ? <Notification bgColor={COLOR.yellow} color={COLOR.white}>
                The assignment was graded without a submission.
            </Notification> : null}
            {is_with_time ? <Notification style={{ display: "flex" }}>
                 <WatchLater style={{ fontSize: "15px", marginLeft: "6px" , marginRight:6 }} />
                This is a timed assessment. Any unanswered questions indicate that the student did not complete the assessment within the allotted time.
            </Notification> : null}
            {waiting_for_ai_grading? <Notification bgColor={COLOR.ai_grade_color} color={COLOR.white}>
                {AI_Review_message}
            </Notification> : null}
            {
                is_loteria && answers && !answers[0]?.is_submitted ? <Alert severity="error" sx={{width: "100%", display: "flex", justifyContent: "center", alignItems: "center", padding: '0px'}}>Not Participated</Alert> : null
            }
            <ExpandedContainer expanded={expanded}>
                <Section>
                    <SectionHeader>Questions & Answers</SectionHeader>
                    {is_graphic_organizer?renderAnswers:questions && questions.map((question, i) =>{
                        let answer =  is_debate || is_loteria ? answers : answers[i]
                        answer = is_worksheet ? answers?.find(k=>question.question_id==k.question_id||question.id == k.question_id ):answer;
                    return (
                        <QuestionComponent
                            key={`${i}-question`}
                            question={question}
                            index={i}
                            answer={answer}
                            allQuestions={questions}
                            is_game={is_game}
                            is_debate={is_debate}
                            is_worksheet={is_worksheet}
                            is_loteria={is_loteria}
                            isStudent={isStudent}
                            selected_student_id={selected_student_id}
                            aiFeedback={ai_feedback ? ai_feedback[i] : null}
                            ai_grade= {ai_grade?ai_grade[i]:null}
                            aiSuggestion={ai_suggestion ? ai_suggestion[i] : null}
                            languageData={languageData}
                            isFeedBack={isFeedBack}
                            renderedGroups={renderedGroups}
                            pauseAllOtherAudios={pauseAllOtherAudios}
                            audioRefs={audioRefs}
                            not_show_indication={not_show_indication}
                            rubricsToggle={rubricsToggle}
                            isCompleted={Boolean(assignment?.waiting_for_ai_grade)}
                        />
                    )})}
                </Section>
                {hasFeedback && <SectionHeader>Teacher Feedback</SectionHeader>}
                {hasFeedback && <FeedbackSection id="teacher-feedback-section">
                    <Section>
                        {feedback ? <FeedbackText> {feedback} </FeedbackText> : null}
                        {audioFeedback ? (() => {
                            const audioKey = `feedback-${audioFeedback}`;
                            if (!audioRefs.current.has(audioKey)) {
                                audioRefs.current.set(audioKey, React.createRef());
                            }
                            const audioRef = audioRefs.current.get(audioKey);
                            return (
                                <TranscriptWithAudio audioUrl={audioFeedback} title={audioFeedback}>
                                    <audio 
                                        ref={audioRef}
                                        controls 
                                        controlsList={"nodownload"} 
                                        src={audioFeedback}
                                        onPlay={() => pauseAllOtherAudios(audioKey)}
                                    />
                                </TranscriptWithAudio>
                            );
                        })() : null}
                        {!audioFeedback && !feedback ? <FeedbackText>No feedback left for this assignment</FeedbackText> : null}
                    </Section>
                </FeedbackSection>}
            </ExpandedContainer>
            </Wrapper>
        </SentryWrap>
    );
};


export default GradeDisplay;
